import { ref, onMounted, readonly } from "vue";
import { createClient } from "@supabase/supabase-js";
import { useDefaultStore } from "~/stores/default";
import { useFunctionsheetStore } from "~/stores/functionsheet";

declare global {
    interface Window {
        supabase: any;
        supabaseState: any;
        supabaseConnectionMonitor: any;
        pendingProcessUpdates: any;
        realtimeCleanupFunctions: any;
    }
}

export default function () {
    // Connection state
    const connectionState = ref({
        isConnected: false,
        lastConnectionTime: null,
        reconnectAttempts: 0,
        maxReconnectAttempts: 5,
        reconnectDelay: 2000, // Base delay in ms
        connectionStatus: "disconnected", // 'disconnected', 'connecting', 'connected', 'error'
        lastError: null,
    });

    // Expose connection state globally for UI components
    if (typeof window !== "undefined") {
        if (!window.supabaseState) {
            window.supabaseState = readonly(connectionState);
        }

        // Initialize pending updates array
        if (!window.pendingProcessUpdates) {
            window.pendingProcessUpdates = [];
        }

        // Initialize cleanup functions array
        if (!window.realtimeCleanupFunctions) {
            window.realtimeCleanupFunctions = [];
        }

        // Listen for online/offline events
        window.addEventListener("online", () => {
            console.log("[useSupabase] Browser went online");
            updateConnectionState("connected");
            // Try to reconnect
            if (window.supabase) {
                try {
                    window.supabase.realtime.connect();
                } catch (error) {
                    console.error("[useSupabase] Failed to reconnect:", error);
                    scheduleReconnect();
                }
            }

            // Process any pending updates when coming back online
            processPendingUpdates();
        });

        window.addEventListener("offline", () => {
            console.log("[useSupabase] Browser went offline");
            updateConnectionState("disconnected");
        });
    }

    async function initSupabase() {
        console.log("[initSupabase] Initializing Supabase client");
        const store = useDefaultStore();
        await store.fetchEssentials();
        if (!store.essentials) return;
        const supabaseUrl = store.essentials.apis.supabase.url;
        const supabaseKey = store.essentials.apis.supabase.key;

        const supabase = createClient(supabaseUrl, supabaseKey);

        // Set up connection state monitoring using a dummy channel
        // This allows us to monitor connection state changes
        const connectionMonitorChannel = supabase
            .channel("connection-monitor")
            .on("system", {}, (payload) => {
                // Handle system events
                console.log("[initSupabase] System event:", payload);
            })
            .subscribe((status) => {
                console.log(`[initSupabase] Connection status: ${status}`);

                if (status === "SUBSCRIBED") {
                    updateConnectionState("connected");
                } else if (
                    status === "CHANNEL_ERROR" ||
                    status === "TIMED_OUT"
                ) {
                    updateConnectionState("error");
                    scheduleReconnect();
                } else if (status === "CLOSED") {
                    updateConnectionState("disconnected");
                    scheduleReconnect();
                }
            });

        // Store the monitor channel for cleanup
        window.supabaseConnectionMonitor = connectionMonitorChannel;

        window.supabase = supabase;
        console.log("[initSupabase] Supabase client created");
        return supabase;
    }

    function updateConnectionState(status, error = null) {
        connectionState.value.connectionStatus = status;
        connectionState.value.isConnected = status === "connected";

        // Add error message if provided
        if (error) {
            connectionState.value.lastError = error;
        } else if (status === "connected") {
            connectionState.value.lastError = null;
        }

        if (status === "connected") {
            connectionState.value.lastConnectionTime = new Date();
            connectionState.value.reconnectAttempts = 0;
        }

        console.log(`[updateConnectionState] Connection status: ${status}`, {
            isConnected: connectionState.value.isConnected,
            reconnectAttempts: connectionState.value.reconnectAttempts,
            lastError: connectionState.value.lastError,
        });
    }

    let reconnectTimer = null;

    function scheduleReconnect() {
        if (reconnectTimer) {
            clearTimeout(reconnectTimer);
        }

        const { reconnectAttempts, maxReconnectAttempts, reconnectDelay } =
            connectionState.value;

        if (reconnectAttempts >= maxReconnectAttempts) {
            console.error(
                "[scheduleReconnect] Max reconnection attempts reached",
            );
            return;
        }

        // Exponential backoff with jitter
        const delay =
            reconnectDelay * Math.pow(2, reconnectAttempts) +
            Math.random() * 1000;

        console.log(
            `[scheduleReconnect] Scheduling reconnection attempt ${reconnectAttempts + 1}/${maxReconnectAttempts} in ${delay}ms`,
        );

        reconnectTimer = setTimeout(() => {
            connectionState.value.reconnectAttempts++;
            updateConnectionState("connecting");

            // Attempt to reinitialize Supabase and channels
            try {
                window.supabase?.realtime.connect();
                updateConnectionState("connected");
            } catch (error) {
                console.error(
                    "[scheduleReconnect] Reconnection failed:",
                    error,
                );
                scheduleReconnect();
            }
        }, delay);
    }

    async function supabaseFetch() {
        try {
            if (!window.supabase) await initSupabase();
            const fsStore = useFunctionsheetStore();
            const store = useDefaultStore();
            // processes table
            if (fsStore.date) {
                const dateobj = new Date(fsStore.date);
                const dayAfter = dateobj.setDate(dateobj.getDate() + 1);
                const dayAfterDate = new Date(dayAfter)
                    .toISOString()
                    .slice(0, 10);

                // TODO: add filter for location
                const { data, error } = await window.supabase
                    .from("processes_new")
                    .select()
                    .gte("date", fsStore.date)
                    .lt("date", dayAfterDate)
                    .eq("location", store.curShop.name);
                if (error) {
                    console.error(error);
                    updateConnectionState("error", error.message);
                    throw error;
                }

                // Update connection state on successful fetch
                updateConnectionState("connected");

                // return data;
                fsStore.supabaseData = data;
            }
        } catch (error) {
            console.error("[supabaseFetch] Error:", error);
            updateConnectionState("error", error.message);
            throw error;
        }
    }

    async function supabaseRealtimeInitProcesses(displayNewProcess) {
        const fsStore = useFunctionsheetStore();
        console.log(
            "[supabaseRealtimeInitProcesses] Creating and subscribing to channel 'db-changes-processes'.",
        );
        console.log(
            "[DEBUG] Realtime subscription setup for date:",
            fsStore.date,
        );

        // Make sure Supabase is initialized
        if (!window.supabase) {
            console.error(
                "[supabaseRealtimeInitProcesses] Supabase client not initialized",
            );
            await initSupabase();
        }

        const channelName = "db-changes-processes";

        // Remove ALL existing channels with this name to avoid duplicates
        const existingChannels = window.supabase
            .getChannels()
            .filter((c) => c.topic === `realtime:${channelName}`);

        if (existingChannels.length > 0) {
            console.log(
                `[supabaseRealtimeInitProcesses] Removing ${existingChannels.length} existing channels: ${channelName}`,
            );
            for (const channel of existingChannels) {
                await window.supabase.removeChannel(channel);
            }
        }

        updateConnectionState("connecting");

        const channel = window.supabase
            .channel(channelName)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "processes_new",
                    // Add filter for current date to reduce unnecessary updates
                    filter: `date=eq.${fsStore.date}`,
                },
                (payload) => {
                    console.log(
                        "[supabaseRealtimeInitProcesses] Received realtime update:",
                        payload,
                    );
                    console.log("[DEBUG] Realtime update details:", {
                        processDate: payload.new?.date,
                        currentDate: fsStore.date,
                        eventType: payload.eventType,
                        processId: payload.new?.id,
                    });
                    try {
                        displayNewProcess(payload);
                    } catch (error) {
                        console.error(
                            "[supabaseRealtimeInitProcesses] Error processing realtime update:",
                            error,
                        );
                    }
                },
            )
            .subscribe((status) => {
                console.log(
                    `[supabaseRealtimeInitProcesses] Subscription status for ${channelName}:`,
                    status,
                );

                if (status === "SUBSCRIBED") {
                    console.log(
                        `[supabaseRealtimeInitProcesses] Successfully subscribed to ${channelName}`,
                    );
                    updateConnectionState("connected");
                    // Re-fetch to catch any changes that happened during subscription setup
                    // This closes the race condition between initial fetch and realtime subscription
                    supabaseFetch().then(() => {
                        console.log(
                            "[supabaseRealtimeInitProcesses] Re-fetched data after subscription confirmed",
                        );
                    });
                } else if (
                    status === "CHANNEL_ERROR" ||
                    status === "TIMED_OUT"
                ) {
                    console.error(
                        `[supabaseRealtimeInitProcesses] Failed to subscribe to ${channelName}: ${status}`,
                    );
                    updateConnectionState("error");
                    scheduleReconnect();
                } else if (status === "CLOSED") {
                    console.log(
                        `[supabaseRealtimeInitProcesses] Subscription closed for ${channelName}`,
                    );
                    updateConnectionState("disconnected");
                    // Schedule reconnection
                    scheduleReconnect();
                }
            });

        // Return cleanup function
        return () => {
            console.log(
                "[supabaseRealtimeInitProcesses] Cleanup: Removing channel 'db-changes-processes'.",
            );
            try {
                window.supabase.removeChannel(channel);
                console.log(
                    "[supabaseRealtimeInitProcesses] Channel removed successfully",
                );
            } catch (error) {
                console.error(
                    "[supabaseRealtimeInitProcesses] Error removing channel:",
                    error,
                );
            }
        };
    }

    async function supabaseFetchRoutes(date) {
        if (!window.supabase) await initSupabase();

        const { data, error } = await window.supabase
            .from("routes")
            .select()
            .eq("date", date)
            .order("created_at", { ascending: true }); // earliest creation first, latest last
        if (error) {
            console.error(error);
            return null;
        }

        // Überprüfen, ob das Array leer ist
        if (Array.isArray(data) && data.length === 0) {
            return null;
        }

        //const routeStore = useRouteStore();
        //routeStore.supabaseData = data;
        return data;
    }

    async function saveRouteToSupabase(date, routes, deliveries, comment) {
        if (!window.supabase) await initSupabase();

        // erstmal speichern wir gesamtes objekt in "data"
        try {
            const { data, error } = await window.supabase
                .from("routes")
                .insert({
                    date: date,
                    routes: routes,
                    deliveries: deliveries,
                    comment: comment,
                })
                .select();
            if (error) {
                throw error;
            }
            console.log("Route added:", JSON.stringify(data, null, 2));
        } catch (error) {
            console.error("Error adding route:", error.message);
        }
    }

    async function updateProtocolInSupabase(
        date,
        routeId,
        entries,
        finished: boolean,
    ) {
        //return; // to not have too many api calls right now
        if (!window.supabase) await initSupabase();

        const store = useDefaultStore();
        console.log("updating or inserting protocol for date: " + date);

        try {
            console.log("checking if entry for this route already exists");
            // check if entry for this route already exists
            const { data: existingData, error: fetchError } =
                await window.supabase
                    .from("route_protocol")
                    .select()
                    .eq("date", date)
                    .eq("routeId", routeId)
                    .single();

            if (fetchError && fetchError.code !== "PGRST116") {
                // PGRST116 is "no rows returned"
                throw fetchError;
            }
            console.log(
                "existingData: " + JSON.stringify(existingData, null, 2),
            );

            if (existingData) {
                console.log("entry for this route already exists");
                // Update existing protocol data
                const { data, error } = await window.supabase
                    .from("route_protocol")
                    .update({
                        entries: entries,
                        finished: finished,
                        username: store.auth.username,
                    })
                    .eq("date", date)
                    .eq("routeId", routeId)
                    .select();

                if (error) throw error;
                console.log("Route updated:", JSON.stringify(data, null, 2));
            } else {
                console.log("no entry for this route found, inserting new row");
                // Insert new row for this route
                const { data, error } = await window.supabase
                    .from("route_protocol")
                    .insert({
                        date: date,
                        routeId: routeId,
                        entries: entries,
                        finished: finished,
                        username: store.auth.username,
                    })
                    .select();

                if (error) throw error;
                console.log("Route added:", JSON.stringify(data, null, 2));
            }
        } catch (error) {
            console.error("Error updating route:", error.message);
        }
    }

    async function supabaseFetchProtocols(date) {
        if (!window.supabase) await initSupabase();

        const { data, error } = await window.supabase
            .from("route_protocol")
            .select()
            .eq("date", date);
        if (error) {
            console.error(error);
            return null;
        }

        return data;
    }

    /**** REALTIME FOR PACKING *****/
    function supabaseRealtimeInitPacking(
        handlePackingProductsUpdate,
        handlePackingsUpdate,
    ) {
        console.log(
            "[useSupabase] supabaseRealtimeInitPacking: Creating and subscribing to channels 'db-changes-packing-products' and 'db-changes-packings'.",
        );
        const channel = window.supabase
            .channel("db-changes-packing-products")
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "packing_products",
                },
                (payload) => handlePackingProductsUpdate(payload),
            )
            .subscribe();
        const channel2 = window.supabase
            .channel("db-changes-packings")
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "packings",
                },
                (payload) => handlePackingsUpdate(payload),
            )
            .subscribe();

        // Return cleanup function
        return () => {
            console.log(
                "[useSupabase] Cleanup: Removing channels 'db-changes-packing-products' and 'db-changes-packings'.",
            );
            window.supabase.removeChannel(channel);
            window.supabase.removeChannel(channel2);
        };
    }

    async function fetchDeliveryRoutes(date) {
        if (!window.supabase) await initSupabase();

        if (!date) {
            throw new Error("Date is required");
        }

        try {
            const { data, error } = await window.supabase
                .from("routes")
                .select("routes, deliveries")
                .eq("date", date)
                .order("created_at", { ascending: false })
                .limit(1);

            if (error) {
                throw new Error(
                    `Error fetching routes from Supabase: ${error.message}`,
                );
            }

            // Return null if no routes found, otherwise return the first (most recent) route
            return data && data.length > 0 ? data[0] : null;
        } catch (err) {
            if (err instanceof Error && err.message.includes("Supabase")) {
                throw err;
            }
            throw new Error("Internal Server Error");
        }
    }

    async function fetchOwntracksLocations(deviceId, limit = 10) {
        if (!window.supabase) await initSupabase();

        if (!deviceId) {
            throw new Error("Device ID is required");
        }

        try {
            const queryBuilder = window.supabase
                .from("owntracks_locations")
                .select("*")
                .eq("device", deviceId)
                .order("tst", { ascending: false })
                .limit(limit);

            const { data, error } = await queryBuilder;

            if (error) {
                throw new Error(
                    `Error fetching locations from Supabase: ${error.message}`,
                );
            }

            return data;
        } catch (err) {
            throw new Error("Internal Server Error");
        }
    }

    // Test network connectivity with enhanced Firefox support
    async function testNetworkConnectivity() {
        try {
            // First check browser's native online status (more reliable in Chrome)
            if (navigator.onLine === false) {
                console.log(
                    "[testNetworkConnectivity] Browser reports offline via navigator.onLine",
                );
                return false;
            }

            // Use multiple fallback methods for better Firefox compatibility
            const testMethods = [
                async () => {
                    const response = await fetch("https://httpbin.org/get", {
                        method: "HEAD",
                        mode: "no-cors",
                        cache: "no-cache",
                        signal: AbortSignal.timeout(2000),
                    });
                    return true;
                },
            ];

            // Try each method until one succeeds
            for (let i = 0; i < testMethods.length; i++) {
                try {
                    console.log(
                        `[testNetworkConnectivity] Trying method ${i + 1}/${testMethods.length}`,
                    );
                    const result = await testMethods[i]();
                    console.log(
                        `[testNetworkConnectivity] Method ${i + 1} succeeded`,
                    );
                    return true;
                } catch (error) {
                    console.log(
                        `[testNetworkConnectivity] Method ${i + 1} failed:`,
                        error.message,
                    );
                    // Continue to next method
                }
            }

            // All methods failed
            console.log(
                "[testNetworkConnectivity] All connectivity test methods failed",
            );
            return false;
        } catch (error) {
            console.log(
                "[testNetworkConnectivity] Network test failed:",
                error.message,
            );
            return false;
        }
    }

    // Monitor connection status with enhanced Firefox support
    function startConnectionMonitoring() {
        // Check connection status more frequently for better Firefox responsiveness
        const intervalId = setInterval(async () => {
            try {
                const isOnline = await testNetworkConnectivity();

                if (isOnline) {
                    // Network is online, check if Supabase is connected
                    const isSupabaseConnected =
                        window.supabase &&
                        window.supabase.realtime.isConnected();

                    if (isSupabaseConnected) {
                        updateConnectionState("connected");
                    } else {
                        updateConnectionState("disconnected");
                        // Try to reconnect
                        if (window.supabase) {
                            try {
                                window.supabase.realtime.connect();
                            } catch (error) {
                                console.error(
                                    "[startConnectionMonitoring] Failed to reconnect:",
                                    error,
                                );
                                updateConnectionState("error", error.message);
                            }
                        }
                    }
                } else {
                    // Network is offline
                    updateConnectionState("disconnected", "Network is offline");
                }
            } catch (error) {
                console.error("[startConnectionMonitoring] Error:", error);
                updateConnectionState("error", error.message);
            }
        }, 15000); // Reduced from 30s to 15s for better Firefox responsiveness

        // Store interval ID for cleanup
        return intervalId;
    }

    // Process pending updates when connection is restored
    async function processPendingUpdates() {
        if (
            !window.pendingProcessUpdates ||
            window.pendingProcessUpdates.length === 0
        ) {
            console.log(
                "[processPendingUpdates] No pending updates to process",
            );
            return;
        }

        console.log(
            `[processPendingUpdates] Processing ${window.pendingProcessUpdates.length} pending updates`,
        );

        // Make a copy of pending updates and clear the array to prevent duplicate processing
        const updatesToProcess = [...window.pendingProcessUpdates];
        window.pendingProcessUpdates = [];

        for (const update of updatesToProcess) {
            try {
                console.log(
                    "[processPendingUpdates] Processing update:",
                    update,
                );

                const { data, error } = await window.supabase
                    .from("processes_new")
                    .upsert(update)
                    .select();

                if (error) {
                    console.error(
                        "[processPendingUpdates] Error processing update:",
                        error,
                    );
                    // Re-add to pending updates for retry
                    window.pendingProcessUpdates.push(update);
                    continue;
                }

                // Verify the update was applied correctly
                if (!data || data.length === 0) {
                    console.error(
                        "[processPendingUpdates] Update returned no data, re-queuing:",
                        update,
                    );
                    window.pendingProcessUpdates.push(update);
                    continue;
                }

                const updatedProcess = data[0];
                const expectedDone = update.done;
                const expectedAborted = update.aborted;

                if (
                    (expectedDone && !updatedProcess.done) ||
                    (expectedAborted && !updatedProcess.aborted)
                ) {
                    console.error(
                        "[processPendingUpdates] Update verification failed, re-queuing:",
                        {
                            update,
                            updatedProcess,
                            expectedDone,
                            expectedAborted,
                            actualDone: updatedProcess.done,
                            actualAborted: updatedProcess.aborted,
                        },
                    );
                    window.pendingProcessUpdates.push(update);
                    continue;
                }

                console.log(
                    "[processPendingUpdates] Successfully processed and verified update:",
                    {
                        processId: update.id,
                        done: updatedProcess.done,
                        aborted: updatedProcess.aborted,
                    },
                );
            } catch (error) {
                console.error(
                    "[processPendingUpdates] Unexpected error processing update:",
                    error,
                );
                // Re-add to pending updates for retry
                window.pendingProcessUpdates.push(update);
            }
        }

        if (window.pendingProcessUpdates.length > 0) {
            console.log(
                `[processPendingUpdates] ${window.pendingProcessUpdates.length} updates failed and will be retried later`,
            );
        }
    }

    return {
        initSupabase,
        supabaseFetch,
        supabaseRealtimeInitProcesses,
        supabaseFetchRoutes,
        saveRouteToSupabase,
        updateProtocolInSupabase,
        supabaseFetchProtocols,
        supabaseRealtimeInitPacking,
        fetchDeliveryRoutes,
        fetchOwntracksLocations,
        connectionState: readonly(connectionState),
        testNetworkConnectivity,
        startConnectionMonitoring,
        processPendingUpdates,
    };
}
